// "Sort with AI" of the positions table: the table's rows are listed for
// the model under short refs, the model hands back an order and grouping
// with the propose_arrangement tool (backend ProposeArrangementTool), and
// the table is rebuilt from its own rows in that order. The content of a
// position never leaves the table, so it cannot change.

export const POSITION_SORT_PURPOSE = 'position_sort';

export const PROPOSE_ARRANGEMENT = 'propose_arrangement';

// limit of POST /ai-chats/{chat}/messages, with room for the header
const MAX_PROMPT_LENGTH = 9500;
const DESCRIPTION_EXCERPT = 80;

const isSpecialRow = (row) =>
  Boolean(row.isPostPosDivider || row.isLastPlaceholder || row.isSum);

// HOAI blocks and time billing lines are generated structures: they stay
// as they are
const isFixedRow = (row) =>
  Boolean(
    row.hoaiType ||
      row.hoaiId ||
      (Array.isArray(row.timeTrackingIds) && row.timeTrackingIds.length),
  );

const depthOf = (row) => Math.max(0, parseInt(row.depth, 10) || 0);

const plainText = (html) =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The table's rows split into what the AI may arrange (the positions
 * before the post-position divider), what stays as it is (HOAI and time
 * billing rows) and the rest (divider, post positions, placeholder, sum).
 */
export function splitRows(rows) {
  const dividerIndex = (rows || []).findIndex((row) => row.isPostPosDivider);
  const before =
    dividerIndex >= 0
      ? rows.slice(0, dividerIndex)
      : (rows || []).filter((row) => !isSpecialRow(row) && !row.isPostPosition);
  const rest =
    dividerIndex >= 0
      ? rows.slice(dividerIndex)
      : (rows || []).filter((row) => isSpecialRow(row) || row.isPostPosition);

  return {
    sortable: before.filter((row) => !isSpecialRow(row) && !isFixedRow(row)),
    fixed: before.filter((row) => !isSpecialRow(row) && isFixedRow(row)),
    rest,
  };
}

const quantityOf = (row) =>
  row.type === 'percent'
    ? `${Number(((Number(row.amount) || 0) * 100).toFixed(2))} %`
    : `${Number(row.amount) || 0} ${row.unit || ''} × ${Number(row.price) || 0} €`;

/**
 * What is sent to the AI: the prompt listing the sortable rows under refs
 * (p1, p2, …), the refs of the positions and of the existing groups for the
 * chat's purpose data, and the snapshot that maps a ref back to a row.
 * Throws "too_many_positions" when the list does not fit the prompt even
 * without descriptions.
 */
export function buildSortRequest(rows, document) {
  const { sortable } = splitRows(rows);
  const snapshot = sortable.map((row, index) => ({
    ref: `p${index + 1}`,
    keyId: row.keyId,
    isGroup: Boolean(row.isGroup),
  }));

  const line = (row, index, withDescription) => {
    const ref = snapshot[index].ref;
    const depth = `depth ${depthOf(row)}`;
    if (row.isGroup) return `${ref} | GROUP | ${row.name || ''} | ${depth}`;
    const excerpt = withDescription
      ? plainText(row.description).slice(0, DESCRIPTION_EXCERPT)
      : '';
    return [
      ref,
      row.name || '',
      quantityOf(row) + (row.isOptional ? ' (optional)' : ''),
      depth,
      ...(excerpt ? [excerpt] : []),
    ].join(' | ');
  };

  const header = `Arrange the positions of this ${document}. One per line: ref | name | quantity × net unit price | depth | start of the description (GROUP marks an existing group). Only the order and the grouping may change.`;
  let prompt = [header, ...sortable.map((row, i) => line(row, i, true))].join(
    '\n',
  );
  if (prompt.length > MAX_PROMPT_LENGTH)
    prompt = [header, ...sortable.map((row, i) => line(row, i, false))].join(
      '\n',
    );
  if (prompt.length > MAX_PROMPT_LENGTH) throw new Error('too_many_positions');

  return {
    prompt,
    refs: snapshot.filter((entry) => !entry.isGroup).map((entry) => entry.ref),
    groupRefs: snapshot
      .filter((entry) => entry.isGroup)
      .map((entry) => entry.ref),
    snapshot,
  };
}

/**
 * The latest propose_arrangement call of the transcript whose stored
 * result reports success: { callId, items }, or null.
 */
export function latestArrangement(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    const calls = message.toolCalls || [];
    for (let j = calls.length - 1; j >= 0; j--) {
      const call = calls[j];
      if (call.name !== PROPOSE_ARRANGEMENT || !Array.isArray(call.arguments?.items))
        continue;
      const result = (message.toolResults || []).find((r) => r.id === call.id);
      if (
        result &&
        !result.failed &&
        !result.denied &&
        typeof result.result === 'string' &&
        !result.result.startsWith('Error:')
      )
        return { callId: call.id, items: call.arguments.items };
    }
  }
  return null;
}

/**
 * The table's rows in the arrangement's order: the sortable rows moved to
 * where the arrangement puts their refs (with the new depth), new groups
 * created from groupDefaults, existing groups the arrangement left out
 * dropped, positions it left out (or that were added while the AI worked)
 * kept at the end, then the fixed rows and the rest as they were.
 */
export function applyArrangement(
  rows,
  items,
  snapshot,
  { groupDefaults = {}, newKeyId },
) {
  const { sortable, fixed, rest } = splitRows(rows);
  const keyByRef = new Map(snapshot.map((entry) => [entry.ref, entry.keyId]));
  const rowByKey = new Map(sortable.map((row) => [row.keyId, row]));
  const used = new Set();
  const arranged = [];

  (items || []).forEach((item) => {
    const depth = Math.max(0, parseInt(item.depth, 10) || 0);
    if (item.group) {
      arranged.push({
        ...groupDefaults,
        name: String(item.group),
        isGroup: true,
        amount: 0,
        price: 0,
        type: 'default',
        depth,
        expandStatus: 2,
        keyId: newKeyId(),
      });
      return;
    }
    const keyId = keyByRef.get(String(item.ref));
    const row = keyId === undefined ? undefined : rowByKey.get(keyId);
    if (!row || used.has(keyId)) return;
    used.add(keyId);
    arranged.push({ ...row, depth });
  });

  sortable.forEach((row) => {
    if (!used.has(row.keyId) && !row.isGroup)
      arranged.push({ ...row, depth: 0 });
  });

  const minDepth = fixed.length ? Math.min(...fixed.map(depthOf)) : 0;
  const fixedRows = fixed.map((row) => ({ ...row, depth: depthOf(row) - minDepth }));

  return [...arranged, ...fixedRows, ...rest];
}
